import React, { useEffect, useState } from 'react'
import adminApi from './api'
import { Notification } from './components/Notification'
import { ConfirmModal } from './components/ConfirmModal'

interface RegionRow {
  id: number
  code: string
  slug: string
  name: { ru?: string; ky?: string; en?: string }
  sortOrder: number
  isDonatable: boolean
  createdAt: string
}

interface RegionStat extends RegionRow {
  totalTrees: number
  totalPlanted: number
  totalAmount: number
  totalDonors: number
}

interface FormData {
  nameRu: string
  nameKy: string
  nameEn: string
  sortOrder: string
  isDonatable: boolean
}

const emptyForm: FormData = {
  nameRu: '',
  nameKy: '',
  nameEn: '',
  sortOrder: '0',
  isDonatable: true
}

// Транслитерация RU → латиница (для code/slug)
function transliterate(text: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo',
    ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
    н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
    ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
    ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    ө: 'o', ү: 'u', ң: 'ng'
  }
  return text
    .toLowerCase()
    .split('')
    .map(c => (map[c] !== undefined ? map[c] : c))
    .join('')
}

function makeSlug(nameRu: string): string {
  const t = transliterate(nameRu)
  return t.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'region'
}

function makeCode(nameRu: string): string {
  const t = transliterate(nameRu)
  return t.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 20) || 'region'
}

export const RegionsAdmin: React.FC = () => {
  const [regions, setRegions] = useState<RegionRow[]>([])
  const [stats, setStats] = useState<Record<number, RegionStat>>({})
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<RegionRow | null>(null)
  const [form, setForm] = useState<FormData>(emptyForm)
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ show: boolean; id: number | null }>({ show: false, id: null })
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)

  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [r1, r2] = await Promise.all([
        adminApi.get<{ regions: RegionRow[] }>('/api/admin/regions'),
        adminApi.get<{ regions: RegionStat[] }>('/api/regions/stats')
      ])
      setRegions(r1.data.regions)
      const statMap: Record<number, RegionStat> = {}
      r2.data.regions.forEach(r => { statMap[r.id] = r })
      setStats(statMap)
    } catch (err: any) {
      setNotification({ message: err?.response?.data?.message || 'Ошибка загрузки', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (region: RegionRow) => {
    setEditing(region)
    setForm({
      nameRu: region.name?.ru || '',
      nameKy: region.name?.ky || '',
      nameEn: region.name?.en || '',
      sortOrder: String(region.sortOrder),
      isDonatable: region.isDonatable !== false
    })
    setModalOpen(true)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nameRu.trim()) {
      setNotification({ message: 'Русское название обязательно', type: 'error' })
      return
    }
    const nameRu = form.nameRu.trim()
    const payload: any = {
      name: {
        ru: nameRu,
        ky: form.nameKy.trim(),
        en: form.nameEn.trim()
      },
      sortOrder: Number(form.sortOrder) || 0,
      isDonatable: form.isDonatable
    }
    // При создании — автогенерация code/slug из русского названия
    if (!editing) {
      payload.code = makeCode(nameRu)
      payload.slug = makeSlug(nameRu)
    }
    try {
      if (editing) {
        await adminApi.patch(`/api/admin/regions/${editing.id}`, payload)
        setNotification({ message: 'Регион обновлён', type: 'success' })
      } else {
        await adminApi.post('/api/admin/regions', payload)
        setNotification({ message: 'Регион создан', type: 'success' })
      }
      setModalOpen(false)
      fetchAll()
    } catch (err: any) {
      const status = err?.response?.status
      let message = err?.response?.data?.message || 'Ошибка сохранения'
      if (status === 409) message = 'Регион с таким названием уже существует'
      setNotification({ message, type: 'error' })
    }
  }

  const deleteRegion = async () => {
    if (!confirmDelete.id) return
    try {
      await adminApi.delete(`/api/admin/regions/${confirmDelete.id}`)
      setNotification({ message: 'Регион удалён', type: 'success' })
      setConfirmDelete({ show: false, id: null })
      fetchAll()
    } catch (err: any) {
      setNotification({ message: err?.response?.data?.message || 'Ошибка удаления', type: 'error' })
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="loading-spinner w-10 h-10"></div>
      </div>
    )
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Регионы</h1>
          <p className="page-subtitle">Области Кыргызстана для карты и статистики</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>Добавить регион</button>
      </div>

      <div className="table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="w-[60px]">#</th>
              <th>Название</th>
              <th className="w-[150px]">Донаты</th>
              <th className="w-[130px]">Деревьев</th>
              <th className="w-[130px]">Доноров</th>
              <th className="w-[50px]"></th>
            </tr>
          </thead>
          <tbody>
            {regions.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <div className="empty-state-text">Регионов пока нет</div>
                  </div>
                </td>
              </tr>
            ) : regions.map(r => (
              <tr key={r.id}>
                <td>{r.sortOrder}</td>
                <td>{r.name?.ru || '—'}</td>
                <td>
                  {r.isDonatable ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e6f4e0] text-[#2f7a1f] px-2.5 py-1 text-xs font-medium">
                      ✓ Открыт
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5f5f5] text-[#8a8a8a] px-2.5 py-1 text-xs font-medium">
                      Скрыт
                    </span>
                  )}
                </td>
                <td>{stats[r.id]?.totalPlanted || stats[r.id]?.totalTrees || 0}</td>
                <td>{stats[r.id]?.totalDonors || 0}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="relative">
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        setOpenMenuId(openMenuId === r.id ? null : r.id)
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-transparent border-0 cursor-pointer text-[20px] text-black hover:bg-[#f5f5f5]"
                      aria-label="Действия"
                    >
                      ⋮
                    </button>
                    {openMenuId === r.id && (
                      <>
                        <div
                          className="fixed inset-0 z-[999] cursor-default"
                          onClick={e => {
                            e.stopPropagation()
                            setOpenMenuId(null)
                          }}
                        />
                        <div className="absolute top-full right-0 mt-1 bg-white border border-[#d9d9d9] rounded-lg shadow-lg z-[1000] min-w-[160px]">
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setOpenMenuId(null)
                              openEdit(r)
                            }}
                            className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-black border-b border-[#f0f0f0] hover:bg-[#f5f5f5]"
                          >
                            Редактировать
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setOpenMenuId(null)
                              setConfirmDelete({ show: true, id: r.id })
                            }}
                            className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                          >
                            Удалить
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Редактировать регион' : 'Новый регион'}</h2>
              <button className="modal-close" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="modal-field">
                  <label className="modal-label">Название (ру) *</label>
                  <input
                    className="form-control"
                    value={form.nameRu}
                    onChange={e => setForm({ ...form, nameRu: e.target.value })}
                    placeholder="Чуйская область"
                    required
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Название (ky)</label>
                  <input
                    className="form-control"
                    value={form.nameKy}
                    onChange={e => setForm({ ...form, nameKy: e.target.value })}
                    placeholder="Чүй облусу"
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Название (en)</label>
                  <input
                    className="form-control"
                    value={form.nameEn}
                    onChange={e => setForm({ ...form, nameEn: e.target.value })}
                    placeholder="Chuy region"
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label">Порядок сортировки</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.sortOrder}
                    onChange={e => setForm({ ...form, sortOrder: e.target.value })}
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-label flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isDonatable}
                      onChange={e => setForm({ ...form, isDonatable: e.target.checked })}
                    />
                    Доступен для донатов
                  </label>
                  <div className="text-xs text-[#8a8a8a] mt-1">
                    Если выключить — регион исчезнет из выпадающего списка на главной странице (Куда посадить?). Существующие донаты и посадки не изменяются.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Регион будет удалён. Все связанные записи (donations/plantings/events) сохранят region_id = NULL."
        onConfirm={deleteRegion}
        onCancel={() => setConfirmDelete({ show: false, id: null })}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />
    </div>
  )
}
